import React, { useEffect, useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CampCard from '../components/camps/CampCard';
import ShareModal from '../components/ShareModal';
import './Favorites.css';
import { useGetFavorites } from '../api';
import { useCampContext } from '../context/CampContext';
import { useUserContext } from '../context/UserContext';
import { useAnalytics } from '../context/AnalyticsContext';
import { Share2, Heart } from 'lucide-react';

const Favorites: React.FC = () => {
  const { favorites, favoritesUuid, setFavorites, setFavoriteUuid, schedule } = useCampContext();
  const { isLoggedIn } = useUserContext();
  const { data, isLoading } = useGetFavorites('1', { enabled: isLoggedIn });
  const [selectedDatesByCampId, setSelectedDatesByCampId] = useState<Record<string, string>>({});
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const { trackPageView } = useAnalytics();

  useEffect(() => {
    trackPageView('/favorites', 'Favorites');
  }, [trackPageView]);
  
  useEffect(() => {
    if (data?.camps) {
      setFavorites(data.camps);
      setFavoriteUuid(data.uuid);
    }
  }, [data, setFavorites]);

  const handleDateChange = (campId: string, dateId: string) => {
    setSelectedDatesByCampId(prev => ({ ...prev, [campId]: dateId }));
  };

  const getScheduledDateIds = (campId: string) => {
    const scheduledDateIds = [];
    if (schedule?.scheduled_camps) {
      for (const scheduled of schedule.scheduled_camps) {
        if (scheduled.camp.id === campId) {
          scheduledDateIds.push(scheduled.camp_date.id);
        }
      }
    }
    return scheduledDateIds;
  };

  return (
    <div className="favorites-page">
      <Header />
      <main className="favorites-main">
        <div className="favorites-container">
          <div className="favorites-header">
            <div className="favorites-title-section">
              <p className="favorites-subtitle">
                {isLoggedIn 
                  ? favorites.length > 0 
                    ? `You have ${favorites.length} saved camp${favorites.length !== 1 ? 's' : ''}`
                    : 'Save camps to build your perfect summer schedule'
                  : 'Sign in to save your favorite camps'
                }
              </p>
            </div>
            {isLoggedIn && favorites.length > 0 && (
              <button 
                className="btn btn--secondary"
                onClick={() => setIsShareModalOpen(true)}
              >
                <Share2 className="icon icon--sm" />
                Share Favorites
              </button>
            )}
          </div>

          {isLoading && (
            <div className="favorites-loading">
              <div className="loading-spinner"></div>
              <p>Loading your favorites...</p>
            </div>
          )}

          {!isLoading && !isLoggedIn && (
            <div className="favorites-empty">
              <div className="empty-state">
                <div className="empty-icon">
                  <Heart size={64} strokeWidth={1.5} />
                </div>
                <h2>Save Your Favorite Camps</h2>
                <p>Sign in to start saving camps and building your perfect summer schedule.</p>
              </div>
            </div>
          )}

          {!isLoading && isLoggedIn && favorites.length === 0 && (
            <div className="favorites-empty">
              <div className="empty-state">
                <div className="empty-icon">
                  <Heart size={64} strokeWidth={1.5} />
                </div>
                <h2>No Favorites Yet</h2>
                <p>Browse camps and click the heart icon to save your favorites here.</p>
                <a href="/search" className="btn btn--primary">
                  Find Camps
                </a>
              </div>
            </div>
          )}

          {!isLoading && isLoggedIn && favorites.length > 0 && (
            <div className="favorites-grid">
              {favorites.map((camp) => (
                <CampCard 
                  key={camp.id} 
                  camp={camp}  
                  favoriteId="1"
                  selectedDate={selectedDatesByCampId[camp.id]}
                  onDateChange={handleDateChange}
                  scheduledDateIds={getScheduledDateIds(camp.id)}
                />
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
      
      {/* Share Modal */}
      <ShareModal 
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        favoriteUuid={favoritesUuid || ''}
      />
    </div>
  );
};

export default Favorites;

